import EF from 'ef/ef';
import Chaplin from 'chaplin';
import PagesCollection from 'ef/collections/pages_collection';
import _ from 'lodash';
import EnrollmentDataService from 'services/data-handler-services/enrollment-data-service';

/*/ (function(EF, undefined){

  // # EF.Models.Activity
  // The activity model is responsible for retrieving information about the pages
  // contained within. There is not much logic associated with the Activities
  // themselves.
  /*/
const Activity = Chaplin.Model.extend({
  initialize: function (attrs, options) {
    options = options || {};
    if (this.collection) {
      this.module = this.collection.module;
      this.set('moduleKey', this.module.get('key'));
    }
    options.activity = this;
    options.module = this.module;
    this.setPages(attrs.pages, options);
    _.bindAll(this, 'attachCompletionEvents');
    this.on('progress_loaded', this.attachCompletionEvents);
  },

  defaults: {
    redo: false,
    completed: false,
  },

  setPages: function (pages, options) {
    this.set('pages', new PagesCollection(pages, options));
    this.pages = this.get('pages');
    this.originalPages = _.clone(this.pages.models);
  },

  getFirstPage: function () {
    return this.pages.at(0);
  },

  attachCompletionEvents: function () {
    const self = this;

    this.set('status', 'incomplete');

    this.on('change:completed', function () {
      if (self.get('completed')) {
        self.set('status', 'complete');
        self.trigger('activity:complete', self);
        self.module.trigger('activity:complete', self);
        EF.trigger('activity:complete', self);
      }
    });

    this.pages.on('change:completed', function () {
      self.set('completed', self.isComplete());
    });

    this.set('completed', this.isComplete());
  },

  // ### getStartingPage
  //
  // Starting page is the last one in the activity that has been completed.
  getStartingPage: function () {
    // if its a redo, if the last page saved isn't the last page, show that one,
    // otherwise show the first page.
    if (this.get('redo')) {
      if (this.get('redoPageKey')) {
        this.currentPage = this.pages.getPageByKey(this.get('redoPageKey'));
      } else {
        this.currentPage = this.pages.head();
      }
    } else {
      // Find the first not complete page
      this.currentPage = this.pages.lastCompleted();
      if (!this.currentPage) {
        this.currentPage = this.pages.head();
      }
    }
    return this.currentPage;
  },

  setPageByKey: function (pageKey) {
    this.currentPage = this.pages.getPageByKey(pageKey);
    return this.currentPage;
  },

  // ### nextPage
  //
  // returns the pageKey of the next page in the activity or false if
  // there is none.
  nextPage: function () {
    const page = this.pages.next(this.currentPage);
    if (page) {
      return page.get('key');
    }
    return false;
  },

  // ### nextPageByKey
  //
  // returns the pageKey of the next page relative to the pageKey passed in
  // or false if there is none.
  nextPageByKey: function (pageKey) {
    const p = this.pages.getPageByKey(pageKey);
    if (p) {
      const page = this.pages.next(p);
      if (page) {
        return page;
      }
    }
    return false;
  },

  // ### getNextPage
  //
  // returns the nextPage and sets it to the currentPage
  getNextPage: function () {
    const page = this.pages.next(this.currentPage);
    if (!EF.router) {
      this.currentPage = page;
    }
    return page;
  },

  // ### previousPage
  //
  // returns the pageKey of the previous page in the activity or false if
  // there is none.
  previousPage: function () {
    return this._previousActivityPage();
  },

  // ### getPreviousPage
  //
  // returns the previousPage in the activity and sets it to the currentPage
  // useful for providing back.
  getPreviousPage: function () {
    return this._previousActivityPage();
  },
  _previousActivityPage: function () {
    const page = this.pages.back(this.currentPage);
    if (page) {
      return page.get('key');
    }
    return false;
  },

  // ### isPageComplete
  //
  // takes a pageKey and returns a boolean
  isPageComplete: function (pageKey) {
    const page = this.pages.getPageByKey(pageKey);
    return page && page.get('completed');
  },

  // ### isComplete
  //
  // Returns a boolean indicating if the activity is complete.
  // Complete means that all the pages in the activity have been completed.
  isComplete: function () {
    return this.pages.every(function (page) {
      return page.get('completed');
    });
  },

  // ### completedPages
  //
  // returns the page keys in the activity that are completed
  completedPages: function () {
    return this.pages.filter(function (page) {
      return page.get('completed');
    });
  },

  // returns the percent complete
  percentComplete: function () {
    return this.completedPages().length / this.pages.length;
  },

  //Sets the users path on this activity by path key
  setPathByKeys: function (pathKeys) {
    const inPathPages = _.filter(this.originalPages, function (page) {
      const pathing = page.get('pathing');
      return _.isEmpty(pathing) || _.intersection(pathing, pathKeys).length > 0;
    });

    this.pages.set(inPathPages);
  },

  resetPathing: function () {
    this.pages.set(this.originalPages);
  },

  previousPageByKey: function (pageKey) {
    const p = this.pages.getPageByKey(pageKey);
    if (p) {
      const page = this.pages.back(p);
      if (page) {
        return page;
      }
    }
    return false;
  },

  getCompletedOptionalPages: function () {
    return EnrollmentDataService.getData('completedOptionalPages') || [];
  },
  isFurtherPageCompleted: function (page) {
    const lastCompleted = this.pages.filter(p => p.get('completed') && !p.get('optional')).slice(-1)[0];
    if (lastCompleted && lastCompleted.get('index')) {
      return page.get('index') < lastCompleted.get('index');
    }
  },
  completeUpToPage: function (page) {
    const completedOptionalPages = this.getCompletedOptionalPages();
    this.pages.each(p => {
      const canComplete =
        !p.get('optional') ||
        completedOptionalPages.includes(p.path()) ||
        (p.get('optional') && this.isFurtherPageCompleted(p));
      if (p.get('index') <= page.get('index') && canComplete) {
        // Silent here bc we don't want to trigger a bunch of shit yet
        // since this happens in the initial setup
        p.set('completed', true, { silent: true });
      }
    });
  },
});

export default Activity;
